import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { apiBaseUrl } from "@/services/apiClient";
import {
  sendVerificationEmail as sendVerificationEmailApi,
  verifyEmail as verifyEmailApi,
} from "@/services/authService";
import { cn } from "@/lib/utils";
import { TURNSTILE_SITE_KEY, turnstileEnabled } from "@/lib/turnstile";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Which entry point opened the modal. Discriminates wizard-gate auth from
   * standalone-login surfaces — currently only used for layout/copy heuristics
   * inside the modal.
   */
  surface: "wizard_submit" | "standalone_login";
  /**
   * Called after a successful email+password register or login. The wizard uses this
   * to re-fire whatever submit handler triggered the modal. The `authPath` discriminator
   * lets the caller fire the registration-conversion analytics event only when a new
   * account was created. NOT called for the Google SSO path — that's a full-page redirect,
   * so the wizard hydrates from IndexedDB on return instead and fires its own conversion
   * event from the post-SSO bootstrap effect.
   */
  onSuccess: (authPath: "register" | "signIn") => void;
  /**
   * Called synchronously right BEFORE we redirect to Google SSO. The wizard uses this
   * to snapshot its in-flight state into IndexedDB so it can hydrate on return. Must
   * be synchronous — once it returns, we navigate away immediately.
   */
  onBeforeSsoRedirect: () => void;
  /** Path the SSO callback should land on after exchanging the code. */
  ssoReturnTo: string;
  /**
   * Externally-controlled processing state — when true the modal replaces its content
   * with a centered loader + label, ignores close attempts, and disables all controls.
   * The wizard sets this between a successful register/signIn and the dLocal redirect
   * or in-app navigation. Keeps the visitor looking at a coherent "preparing your
   * request" surface instead of a brief flash during the post-auth submit.
   */
  forceProcessing?: boolean;
  forceProcessingLabel?: string;
}

type AuthMode = "register" | "signIn";
type RegisterPhase = "form" | "code";

const RESEND_COOLDOWN_SECONDS = 10;

function getApiErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { message?: unknown } } }).response?.data;
    const m = data?.message;
    if (typeof m === "string") return m;
    if (Array.isArray(m)) return m.join(", ");
  }
  return undefined;
}

function GoogleGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function AuthModal({
  open,
  onOpenChange,
  onSuccess,
  onBeforeSsoRedirect,
  ssoReturnTo,
  surface: _surface,
  forceProcessing = false,
  forceProcessingLabel,
}: AuthModalProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { registerAndLogin, loginWithPassword } = useAuth();
  const [mode, setMode] = useState<AuthMode>("register");
  const [phase, setPhase] = useState<RegisterPhase>("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleRedirecting, setGoogleRedirecting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const isRegister = mode === "register";

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  }, []);

  // Reset to the form step whenever the user switches between register / sign-in or
  // closes + re-opens the modal. Avoids being stuck on the code step after backing out.
  useEffect(() => {
    if (!open) {
      setPhase("form");
      setVerificationCode("");
      setResendCooldown(0);
      setTurnstileToken(null);
    }
  }, [open]);

  useEffect(() => {
    setPhase("form");
    setVerificationCode("");
    setTurnstileToken(null);
  }, [mode]);

  // Resend-cooldown ticker.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const canSubmitForm =
    !sendingCode &&
    !submitting &&
    !googleRedirecting &&
    email.trim().length > 0 &&
    password.length > 0 &&
    (!isRegister || (firstName.trim().length > 0 && lastName.trim().length > 0 && password.length >= 8));

  const canVerify =
    !verifying &&
    verificationCode.trim().length === 6 &&
    (!turnstileEnabled || turnstileToken != null);

  const handleGoogle = (): void => {
    if (googleRedirecting || submitting || sendingCode || verifying) return;
    setGoogleRedirecting(true);
    onBeforeSsoRedirect();
    // Append `postSsoResume=1` so the wizard's mount effect can detect that this
    // navigation is the tail of an SSO round-trip and auto-fire the pending submit
    // (otherwise the visitor has to manually click "Pay" again after landing back
    // on the summary — see bug report 2026-05-13).
    const returnTo = ssoReturnTo.includes("?")
      ? `${ssoReturnTo}&postSsoResume=1`
      : `${ssoReturnTo}?postSsoResume=1`;
    window.location.href = `${apiBaseUrl}/auth/sso/google?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!canSubmitForm) return;

    if (!isRegister) {
      setSubmitting(true);
      try {
        const result = await loginWithPassword(email, password);
        if ("pendingLoginId" in result) {
          toast.error(
            t(
              "authModal.signIn.platformUserBlocked",
              "Esta cuenta es de operadores. Iniciá sesión desde el portal correspondiente.",
            ),
          );
          return;
        }
        toast.success(t("authModal.signIn.success", "Bienvenido/a"));
        onSuccess("signIn");
      } catch (err) {
        const msg = getApiErrorMessage(err)?.toLowerCase() ?? "";
        if (msg.includes("invalid") || msg.includes("credentials")) {
          toast.error(
            t("authModal.signIn.invalidCredentials", "Email o contraseña incorrectos."),
          );
        } else {
          toast.error(
            t("authModal.signIn.error", "No pudimos iniciar sesión. Intentalo de nuevo."),
          );
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Register flow: send code → switch to verify step.
    setSendingCode(true);
    try {
      await sendVerificationEmailApi(email, i18n.language);
      setPhase("code");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success(
        t("authModal.register.codeSent", "Te enviamos un código a tu email."),
      );
    } catch (err) {
      const msg = getApiErrorMessage(err)?.toLowerCase() ?? "";
      if (msg.includes("already registered") || msg.includes("already exists")) {
        toast.error(
          t(
            "authModal.register.alreadyRegistered",
            "Ya existe una cuenta con este email. Iniciá sesión para continuar.",
          ),
        );
        setMode("signIn");
      } else {
        toast.error(
          t(
            "authModal.register.sendCodeError",
            "No pudimos enviar el código. Intentalo de nuevo.",
          ),
        );
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (resendCooldown > 0 || resendingCode) return;
    setResendingCode(true);
    try {
      await sendVerificationEmailApi(email, i18n.language);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success(t("authModal.register.codeResent", "Código reenviado."));
    } catch {
      toast.error(
        t("authModal.register.resendCodeError", "No pudimos reenviar el código."),
      );
    } finally {
      setResendingCode(false);
    }
  };

  const handleVerifyAndRegister = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!canVerify) return;
    setVerifying(true);
    try {
      const { registrationToken } = await verifyEmailApi(email, verificationCode.trim());
      // accountKind: 'person' — the backend defaults to this now, but we
      // send it explicitly so older backend builds still create the user
      // with the expected kind. The post-register account-type picker
      // has been retired from the consumer surfaces.
      await registerAndLogin(
        email,
        password,
        registrationToken,
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          accountKind: "person",
        },
        turnstileToken ?? undefined,
      );
      toast.success(t("authModal.register.success", "Cuenta verificada"));
      onSuccess("register");
    } catch (err) {
      const msg = getApiErrorMessage(err)?.toLowerCase() ?? "";
      if (msg.includes("captcha")) {
        resetTurnstile();
        toast.error(
          t(
            "authModal.register.captchaFailed",
            "No pudimos validar la verificación de seguridad. Intentalo de nuevo.",
          ),
        );
      } else if (msg.includes("invalid") || msg.includes("expired") || msg.includes("code")) {
        toast.error(
          t("authModal.register.invalidCode", "Código inválido o expirado."),
        );
      } else if (msg.includes("already registered") || msg.includes("already exists")) {
        toast.error(
          t(
            "authModal.register.alreadyRegistered",
            "Ya existe una cuenta con este email. Iniciá sesión para continuar.",
          ),
        );
        setMode("signIn");
        setPhase("form");
      } else {
        toast.error(
          t("authModal.register.error", "No pudimos completar el registro."),
        );
      }
    } finally {
      setVerifying(false);
    }
  };

  if (forceProcessing) {
    return (
      <Dialog open={open} onOpenChange={() => undefined}>
        <DialogContent
          className="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="text-xl">
              {t("authModal.processing.title", "Preparando tu solicitud")}
            </DialogTitle>
            <DialogDescription>
              {forceProcessingLabel ??
                t(
                  "authModal.processing.description",
                  "Te estamos enviando al pago. Esto puede tomar unos segundos.",
                )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // -----------------------------------------------------------------------
  // Verify-code step (register only)
  // -----------------------------------------------------------------------

  if (isRegister && phase === "code") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-md">
          <DialogHeader className="space-y-1.5 text-left">
            <button
              type="button"
              onClick={() => {
                setPhase("form");
                setVerificationCode("");
                setTurnstileToken(null);
              }}
              className="-ml-1 inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("authModal.code.back", "Cambiar datos")}
            </button>
            <DialogTitle className="text-xl">
              {t("authModal.code.title", "Verificá tu email")}
            </DialogTitle>
            <DialogDescription>
              {t("authModal.code.description", "Te enviamos un código de 6 dígitos a")}{" "}
              <span className="font-medium text-foreground">{email}</span>.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-3" onSubmit={handleVerifyAndRegister}>
            <div className="space-y-1.5">
              <Label htmlFor="authmodal-code">
                {t("authModal.code.label", "Código de verificación")}
              </Label>
              <Input
                id="authmodal-code"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                disabled={verifying}
                placeholder="000000"
                className="text-center font-mono text-lg tracking-[0.5em]"
                required
              />
            </div>

            {turnstileEnabled ? (
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={handleTurnstileSuccess}
                  onExpire={handleTurnstileExpire}
                  options={{
                    language: i18n.language.startsWith("es") ? "es" : "en",
                    theme: "light",
                  }}
                />
              </div>
            ) : null}

            <Button type="submit" className="w-full" disabled={!canVerify}>
              {verifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("authModal.code.verifying", "Verificando…")}
                </>
              ) : (
                t("authModal.code.submit", "Verificar y continuar")
              )}
            </Button>

            <div className="text-center text-xs text-muted-foreground">
              {resendCooldown > 0 ? (
                t("authModal.code.resendCooldown", "Reenviar código en {{s}}s", {
                  s: resendCooldown,
                })
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendingCode}
                  className="font-medium text-foreground underline-offset-2 hover:underline disabled:opacity-50"
                >
                  {resendingCode
                    ? t("authModal.code.resending", "Reenviando…")
                    : t("authModal.code.resend", "Reenviar código")}
                </button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // -----------------------------------------------------------------------
  // Form step (register OR sign-in)
  // -----------------------------------------------------------------------

  const formSubmitLabel = isRegister
    ? sendingCode
      ? t("authModal.register.sending", "Enviando código…")
      : t("authModal.register.submit", "Crear cuenta")
    : submitting
      ? t("authModal.signIn.submitting", "Ingresando…")
      : t("authModal.signIn.submit", "Iniciar sesión y continuar");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-md">
        <DialogHeader className="space-y-1.5 text-left">
          <DialogTitle className="text-xl">
            {isRegister
              ? t("authModal.title.register", "Crear cuenta para continuar")
              : t("authModal.title.signIn", "Iniciar sesión para continuar")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "authModal.description",
              "La necesitamos para asociar tu solicitud y enviarte el comprobante del pago.",
            )}
          </DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={googleRedirecting || submitting || sendingCode}
          className={cn(
            "mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md border bg-background px-4 py-2.5 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          {googleRedirecting ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          ) : (
            <GoogleGlyph />
          )}
          {googleRedirecting
            ? t("authModal.google.redirecting", "Redirigiendo…")
            : t("authModal.google.cta", "Continuar con Google")}
        </button>

        <div className="relative my-1 text-center text-xs">
          <span className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </span>
          <span className="relative bg-background px-2 text-muted-foreground">
            {t("authModal.divider", "o usá tu email")}
          </span>
        </div>

        <form className="space-y-3" onSubmit={handleFormSubmit}>
          {isRegister ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="authmodal-firstname">
                  {t("authModal.fields.firstName", "Nombre")}
                </Label>
                <Input
                  id="authmodal-firstname"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={sendingCode}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="authmodal-lastname">
                  {t("authModal.fields.lastName", "Apellido")}
                </Label>
                <Input
                  id="authmodal-lastname"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={sendingCode}
                  required
                />
              </div>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="authmodal-email">
              {t("authModal.fields.email", "Email")}
            </Label>
            <Input
              id="authmodal-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={sendingCode || submitting}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="authmodal-password">
              {t("authModal.fields.password", "Contraseña")}
            </Label>
            <PasswordInput
              id="authmodal-password"
              autoComplete={isRegister ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={sendingCode || submitting}
              minLength={isRegister ? 8 : undefined}
              required
            />
            {isRegister ? (
              <p className="text-xs text-muted-foreground">
                {t("authModal.register.passwordHint", "Mínimo 8 caracteres.")}
              </p>
            ) : null}
          </div>

          <Button type="submit" className="w-full" disabled={!canSubmitForm}>
            {(sendingCode || submitting) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {formSubmitLabel}
              </>
            ) : (
              formSubmitLabel
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          {isRegister ? (
            <>
              {t("authModal.switch.haveAccount", "¿Ya tenés cuenta?")}{" "}
              <button
                type="button"
                onClick={() => setMode("signIn")}
                className="font-medium text-foreground underline-offset-2 hover:underline"
              >
                {t("authModal.switch.toSignIn", "Iniciar sesión")}
              </button>
            </>
          ) : (
            <>
              {t("authModal.switch.noAccount", "¿No tenés cuenta?")}{" "}
              <button
                type="button"
                onClick={() => setMode("register")}
                className="font-medium text-foreground underline-offset-2 hover:underline"
              >
                {t("authModal.switch.toRegister", "Crear cuenta")}
              </button>
            </>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
